import { useState } from 'react'
import { Autocomplete, CircularProgress, FilterOptionsState } from '@mui/material'
import { Grid, TextField } from '@dhl-ui/components'
import {
  OrganisationDto,
  ParcelShop,
  useGetOrganisations,
  useGetParcelShops,
  useGetTerminalOrganisations,
  type UserType,
  useUpdateOrganisationForUser,
  useUpdateParcelShopForUser,
  useUpdateTerminalOrganisationForUser,
} from '@dhl/mdp-api'
import { useDebounce } from '@dhl/utils'
import { useIsLoading } from '~/common/hooks'
import type { LabelValue, UUID } from '~/common/model'
import { sortByName } from '~/common/util/arrayUtil'
import type { User } from '~/modules/users/model'

type Props = {
  user: User
  onOrganisationChanged: (id: UUID, newUserType: UserType, newOrganisationId: string) => void
}

export const EditUserOrganisation = ({ user, onOrganisationChanged }: Props) => {
  const { id, userType, parcelShopName, parcelShopPsfKey, organisationName, organisationId } = user
  const { isLoading, setIsLoading } = useIsLoading()

  const { mutateAsync: updateParcelShopForUser } = useUpdateParcelShopForUser()
  const { mutateAsync: updateOrganisationForUser } = useUpdateOrganisationForUser()
  const { mutateAsync: updateTerminalForUser } = useUpdateTerminalOrganisationForUser()

  const postUserOrganisation = async (newOrganisationId: string, newUserType: UserType) => {
    setIsLoading(true)
    switch (newUserType) {
      case 'parcelShop':
        await updateParcelShopForUser({ emailOrId: id, parcelShopPsfKey: newOrganisationId })
        break
      case 'business':
        await updateOrganisationForUser({ emailOrId: id, organisationId: newOrganisationId })
        break
      case 'terminal':
        await updateTerminalForUser({
          emailOrId: id,
          organisationId: newOrganisationId,
        })
        break
      default:
        return
    }
    onOrganisationChanged(id, newUserType, newOrganisationId)
    setIsLoading(false)
  }

  const showParcelShopDropDown = userType === 'consumer' || userType === 'parcelShop'
  const showOrganisationDropDown = userType === 'consumer' || userType === 'business'
  const showTerminalsDropDown = userType === 'consumer' || userType === 'terminal'

  return (
    <>
      <Grid container direction="column">
        <Grid>
          {showParcelShopDropDown && (
            <OrganisationAutoComplete
              key="parcelshopDD"
              id="parcelshops"
              label="Parcelshop"
              url="parcel-shops"
              defaultValue={{ label: parcelShopName || '', value: parcelShopPsfKey || '' }}
              labelValueMapper={(p: ParcelShop) => ({ label: p.name, value: p.parcelShopPsfKey })}
              onChange={(parcelShopPsfKey) => postUserOrganisation(parcelShopPsfKey, 'parcelShop')}
            />
          )}
        </Grid>
        <Grid>
          {showOrganisationDropDown && (
            <OrganisationAutoComplete
              key="organisationDD"
              id="organisation"
              label="Organisation"
              url="organisation"
              defaultValue={{ label: organisationName || '', value: organisationId || '' }}
              labelValueMapper={(o: OrganisationDto) => ({ label: o.name, value: o.id })}
              onChange={(id) => postUserOrganisation(id, 'business')}
            />
          )}
        </Grid>
        <Grid>
          {showTerminalsDropDown && (
            <OrganisationAutoComplete
              key="terminalDD"
              id="terminal"
              label="Terminal"
              url="terminal"
              defaultValue={{ label: organisationName || '', value: organisationId || '' }}
              labelValueMapper={(o: OrganisationDto) => ({ label: o.name, value: o.id })}
              onChange={(id) => postUserOrganisation(id, 'terminal')}
            />
          )}
        </Grid>
      </Grid>
    </>
  )
}

type OrganisationAutoCompleteProps = {
  id: string
  label: string
  url: 'parcel-shops' | 'organisation' | 'terminal'
  labelValueMapper: (dto: any) => LabelValue
  defaultValue?: LabelValue
  loading?: boolean
  onChange: (value: string) => void
}
const OrganisationAutoComplete = ({
  id,
  label,
  url,
  labelValueMapper,
  defaultValue = { label: '', value: '' },
  onChange,
}: OrganisationAutoCompleteProps) => {
  const [query, setQuery] = useState<string>('')
  const debouncedQuery = useDebounce(query, 300)

  const params = { query: debouncedQuery, max: 50, skip: 0 }
  const getQueryOptions = (match: string) => {
    return {
      query: {
        enabled: url === match,
      },
    }
  }
  const { data: parcelShops, isLoading: isLoadingParcelShops } = useGetParcelShops(
    params,
    getQueryOptions('parcel-shops')
  )
  const { data: organisations, isLoading: isLoadingOrganisations } = useGetOrganisations(
    params,
    getQueryOptions('organisation')
  )
  const { data: terminals, isLoading: isLoadingTerminals } = useGetTerminalOrganisations(
    params,
    getQueryOptions('terminal')
  )
  const isLoading = isLoadingParcelShops || isLoadingOrganisations || isLoadingTerminals

  const getOptions = (): LabelValue[] => {
    const getData = () => {
      switch (url) {
        case 'parcel-shops':
          return parcelShops?.content || []
        case 'organisation':
          return organisations?.content || []
        case 'terminal':
          return terminals?.content || []
        default:
          return []
      }
    }
    return getData().sort(sortByName).map(labelValueMapper)
  }

  const handleFilterOptions = (options: LabelValue[], state: FilterOptionsState<LabelValue>) => {
    const search = state.inputValue.toLowerCase()
    setQuery(search)
    return options
  }

  return (
    <Autocomplete
      id={id}
      data-testid={`organisation-autocomplete-${url}`}
      defaultValue={defaultValue}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      options={getOptions()}
      filterOptions={handleFilterOptions}
      loading={isLoading}
      getOptionLabel={(option) => option.label}
      onChange={(_, labelValue) => {
        if (labelValue) {
          onChange(labelValue.value)
        }
      }}
      fullWidth
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  )
}
